import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LocizeBackend, { LocizeBackendOptions } from 'i18next-locize-backend';
import HttpBackend, { HttpBackendOptions } from 'i18next-http-backend';
import { DEVELOPMENT } from './utils/constants';
import LanguageDetector from 'i18next-browser-languagedetector';
import { DateTime } from 'luxon';
import { i18nSupportedLanguageCodes, resolveLocaleFolder } from './utils/language-config';
import { isBrowserOffline } from './utils/networkStatus';

const LOCIZE_API_KEY = DEVELOPMENT ? '7bfa07db-cb54-4861-b147-4637adad80e5' : undefined;
const LOCIZE_PROJECT_KEY = 'ad65a48a-898e-4338-b4fa-2fd56fb722ab';
const useRemoteTranslations = DEVELOPMENT && !isBrowserOffline();

const locizeOptions: LocizeBackendOptions = {
  projectId: LOCIZE_PROJECT_KEY,
  apiKey: LOCIZE_API_KEY,
  referenceLng: 'en',
  allowedAddOrUpdateHosts: ['bps.sphero.com', 'localhost'],
};

const localOptions: HttpBackendOptions = {
  loadPath: (lngs, namespaces) => {
    const language = lngs[0] ?? 'en';
    const namespace = namespaces[0] ?? 'translation';
    return `/locales/${resolveLocaleFolder(language)}/${namespace}.json`;
  },
  // Locale JSON lives under public/ with stable URLs (no build hash). Browsers and
  // CDNs cache those GETs aggressively; bypass cache so deploys show new copy.
  // Same option is set in web-edu
  requestOptions: { cache: 'no-store' },
};

// All Luxon preset format keys supported in translation strings.
// Usage in translations: {{someDate, DATE_MED}} or {{someDate, DATETIME_FULL}} etc.
const LUXON_FORMAT_KEYS = [
  'DATE_SHORT',
  'DATE_MED',
  'DATE_MED_WITH_WEEKDAY',
  'DATE_FULL',
  'DATE_HUGE',
  'TIME_SIMPLE',
  'TIME_WITH_SECONDS',
  'TIME_WITH_SHORT_OFFSET',
  'TIME_WITH_LONG_OFFSET',
  'TIME_24_SIMPLE',
  'TIME_24_WITH_SECONDS',
  'DATETIME_SHORT',
  'DATETIME_SHORT_WITH_SECONDS',
  'DATETIME_MED',
  'DATETIME_MED_WITH_SECONDS',
  'DATETIME_FULL',
  'DATETIME_FULL_WITH_SECONDS',
  'DATETIME_HUGE',
  'DATETIME_HUGE_WITH_SECONDS',
] as const;

const languageDetector = new LanguageDetector(null, {
  order: ['querystring', 'cookie', 'navigator', 'localStorage', 'sessionStorage', 'htmlTag', 'path', 'subdomain'],
});

if (useRemoteTranslations) {
  i18n.use(LocizeBackend);
} else {
  i18n.use(HttpBackend);
}

const i18nInitPromise = i18n
  .use(initReactI18next)
  .use(languageDetector)
  .init({
    backend: useRemoteTranslations ? locizeOptions : localOptions,
    fallbackLng: {
      default: ['en'],
    },
    ns: ['translation'],
    defaultNS: 'translation',
    supportedLngs: [...i18nSupportedLanguageCodes],
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false, // React already escapes output
    },
    maxRetries: useRemoteTranslations ? 1 : 0,
    retryTimeout: 200,
    saveMissing: useRemoteTranslations, // Only upload missing keys when the dev Locize backend is active.
    debug: DEVELOPMENT,
  })
  .then(() => {
    const formatter = i18n.services.formatter;
    if (!formatter) {
      console.warn('[i18n] Formatter service unavailable; skipping Luxon formatter registration.');
      return;
    }

    // Register Luxon date formatters using the i18next v23+ formatter API.
    // Translation strings reference these by key: {{someDate, DATETIME_MED}}
    for (const key of LUXON_FORMAT_KEYS) {
      formatter.add(key, (value: unknown, lng: string | undefined) => {
        if (!(value instanceof Date)) return String(value);
        return DateTime.fromJSDate(value)
          .setLocale(lng ?? 'en')
          .toLocaleString(DateTime[key] as Intl.DateTimeFormatOptions);
      });
    }
  });

i18n.on('failedLoading', (lng, ns, msg) => {
  console.warn(`[i18n] Failed loading namespace "${ns}" for language "${lng}": ${msg}`);
});

export async function ensureI18nInitialized(): Promise<void> {
  if (i18n.isInitialized) {
    return;
  }

  await i18nInitPromise;
}

export default i18n;
